<template>
    <div class="action-buttons">
        <input ref="fileInput" type="file" accept=".wav" @change="onFileChange" hidden />
        <button class="upload-btn" @click="onUploadClick">Upload<i class="fas fa-upload"></i></button>
        <button v-if="isRecording" class="record-btn stop-btn" @click="onRecordClick">
            Stop<i class="fas fa-stop"></i>
        </button>
        <button v-else class="record-btn" @click="onRecordClick">
            Record<i class="fas fa-microphone"></i>
        </button>
    </div>
    <div v-if="!history.length" class="audio-history-empty">
        <img :src="iconEmpty" alt="Empty" class="empty-img" />
        <div class="empty-text">
            There is no voice here, only the sound of silence.
        </div>
    </div>
    <div class="audio-history" v-else>
        <div class="audio-history-title">Audio History</div>
        <div v-for="(item, idx) in history" :key="idx" class="audio-history-row"
            :class="{ selected: selectedIdx === idx }">
            <span class="audio-history-time">{{ item.createdAt.toLocaleString() }}</span>
            <span class="audio-history-duration">{{ formatTime(item.duration) }}</span>
            <div class='select-btn-area'>
                <div class="select-btn" :class="{ selected: selectedIdx === idx }" @click="selectHistory(item, idx)">
                    {{ selectedIdx === idx ? 'Selected' : 'Use' }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, nextTick, defineComponent, onBeforeUnmount } from 'vue';
import iconEmpty from '@/assets/icons/iconEmpty.png';

export default defineComponent({
    name: 'RecordUploadView',
    emits: ['audio-blob', 'confirm', 'cancel'],


    setup(props, { emit }) {
        const fileInput = ref(null);
        const isRecording = ref(false);
        const recordTime = ref(0);
        const waveCanvas = ref(null);
        const mediaRecorder = ref(null);
        const mediaStream = ref(null);
        const chunks = [];
        let recorderTimerInterval = null;
        let audioCtx = null;
        let analyser = null;
        let dataArray = null;
        let sourceNode = null;
        let rafId = null;
        const history = ref([]);
        const selectedIdx = ref(-1);

        function formatTime(sec) {
            if (!isFinite(sec) || isNaN(sec) || sec <= 0) return '00:00';
            const m = Math.floor(sec / 60).toString().padStart(2, '0');
            const s = Math.floor(sec % 60).toString().padStart(2, '0');
            return `${m}:${s}`;
        }
        function onUploadClick() {
            fileInput.value.click();
        }
        async function onFileChange(event) {
            const file = event.target.files[0];
            if (file) {
                const trimmed = await trimAudioBlob(file);
                emit('audio-blob', trimmed);
                const now = new Date();
                const duration = await getAudioDuration(trimmed);
                history.value.unshift({
                    blob: trimmed,
                    createdAt: now,
                    duration,
                    url: window.URL.createObjectURL(trimmed),
                });
                selectedIdx.value = 0; // <-- select the new item
            }
        }

        async function onRecordClick() {
            if (isRecording.value) {
                stopRecording();
                return;
            }
            startRecording();
        }

        async function startRecording() {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaStream.value = stream;
            audioCtx = new (window.AudioContext || window.webkitAudioContext)();
            analyser = audioCtx.createAnalyser();
            analyser.fftSize = 2048;
            dataArray = new Uint8Array(analyser.fftSize);
            sourceNode = audioCtx.createMediaStreamSource(stream);
            sourceNode.connect(analyser);
            isRecording.value = true;
            await nextTick();
            drawWaveform();
            mediaRecorder.value = new MediaRecorder(stream);
            mediaRecorder.value.ondataavailable = e => chunks.push(e.data);
            mediaRecorder.value.onstop = async () => {
                const blob = new Blob(chunks);
                const trimmed = await trimAudioBlob(blob);
                emit('audio-blob', trimmed);
                const now = new Date();
                const duration = await getAudioDuration(trimmed);
                history.value.unshift({
                    blob: trimmed,
                    createdAt: now,
                    duration,
                    url: window.URL.createObjectURL(trimmed),
                });
                selectedIdx.value = 0; // <-- select the new item
                chunks.length = 0;
            };
            mediaRecorder.value.start();
            recordTime.value = 0;
            recorderTimerInterval = setInterval(() => recordTime.value++, 1000);
        }

        function stopRecording() {
            mediaRecorder.value.stop();
            if (mediaStream.value) {
                mediaStream.value.getTracks().forEach(track => track.stop());
                mediaStream.value = null;
            }
            cancelAnimationFrame(rafId);
            if (audioCtx) audioCtx.close();
            clearInterval(recorderTimerInterval);
            recordTime.value = 0;
            isRecording.value = false;
        }

        function drawWaveform() {
            if (!waveCanvas.value) return;
            rafId = requestAnimationFrame(drawWaveform);
            analyser.getByteTimeDomainData(dataArray);
            const canvas = waveCanvas.value;
            const ctx = canvas.getContext('2d');
            const { width, height } = canvas;
            ctx.clearRect(0, 0, width, height);
            ctx.lineWidth = 2;
            ctx.beginPath();
            const sliceWidth = width / dataArray.length;
            let x = 0;
            for (let i = 0; i < dataArray.length; i++) {
                const v = dataArray[i] / 128.0;
                const y = (v * height) / 2;
                if (i === 0) ctx.moveTo(x, y);
                else ctx.lineTo(x, y);
                x += sliceWidth;
            }
            ctx.stroke();
        }
        async function trimAudioBlob(blob) {
            const arrayBuffer = await blob.arrayBuffer();
            const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
            const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
            const maxDur = 29;
            const targetSampleRate = 24000;

            function writeString(view, offset, str) {
                for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
            }
            function bufferToWav(buffer) {
                const numChan = buffer.numberOfChannels;
                const length = buffer.length * numChan * 2 + 44;
                const view = new DataView(new ArrayBuffer(length));
                writeString(view, 0, 'RIFF');
                view.setUint32(4, 36 + buffer.length * numChan * 2, true);
                writeString(view, 8, 'WAVE');
                writeString(view, 12, 'fmt ');
                view.setUint32(16, 16, true);
                view.setUint16(20, 1, true);
                view.setUint16(22, numChan, true);
                view.setUint32(24, buffer.sampleRate, true);
                view.setUint32(28, buffer.sampleRate * numChan * 2, true);
                view.setUint16(32, numChan * 2, true);
                view.setUint16(34, 16, true);
                writeString(view, 36, 'data');
                view.setUint32(40, buffer.length * numChan * 2, true);
                let offset = 44;
                for (let i = 0; i < buffer.length; i++) {
                    for (let ch = 0; ch < numChan; ch++) {
                        let s = buffer.getChannelData(ch)[i];
                        s = Math.max(-1, Math.min(1, s));
                        view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
                        offset += 2;
                    }
                }
                return new Blob([view], { type: 'audio/wav' });
            }

            const duration = Math.min(audioBuffer.duration, maxDur);
            const startOffset = audioBuffer.duration > maxDur ? audioBuffer.duration - maxDur : 0;
            const frameCount = Math.ceil(duration * targetSampleRate);
            const offlineCtx = new OfflineAudioContext(1, frameCount, targetSampleRate);
            const src = offlineCtx.createBufferSource();
            src.buffer = audioBuffer;
            src.connect(offlineCtx.destination);
            src.start(0, startOffset, duration);
            const rendered = await offlineCtx.startRendering();
            return bufferToWav(rendered);
        }
        async function getAudioDuration(blob) {
            return new Promise((resolve) => {
                const audio = document.createElement('audio');
                const url = URL.createObjectURL(blob);
                audio.src = url;
                audio.addEventListener('loadedmetadata', () => {
                    URL.revokeObjectURL(url);
                    // If duration is Infinity or NaN, set to 0
                    if (!isFinite(audio.duration) || isNaN(audio.duration)) {
                        resolve(0);
                    } else {
                        resolve(audio.duration);
                    }
                });
                // Fallback: if metadata doesn't load in 2s, resolve 0
                setTimeout(() => resolve(0), 2000);
            });
        }

        function selectHistory(item, idx) {
            selectedIdx.value = idx;
            console.log(idx)
            emit('audio-blob', item.blob);
        }

        onBeforeUnmount(() => {
            cancelAnimationFrame(rafId);
            if (mediaRecorder.value && mediaRecorder.value.state !== 'inactive') {
                mediaRecorder.value.stop();
            }
            if (mediaStream.value) {
                mediaStream.value.getTracks().forEach(track => track.stop());
                mediaStream.value = null;
            }
            // Only close if audioCtx exists and is not already closed
            if (audioCtx && audioCtx.state !== 'closed') {
                audioCtx.close();
            }
            // Revoke all object URLs
            history.value.forEach(item => {
                if (item.url) window.URL.revokeObjectURL(item.url);
            });
        });
        return {
            fileInput,
            isRecording,
            recordTime,
            waveCanvas,
            formatTime,
            onUploadClick,
            onFileChange,
            onRecordClick,
            history,
            selectHistory,
            selectedIdx,
            iconEmpty, // add this
        };
    }
});

</script>

<style scoped>
.action-buttons {
    display: flex;
    justify-content: flex-end;
    align-items: right;
    width: 100%;
    margin-top: 5px;
    gap: 16px;
}

.upload-btn,
.record-btn {
    display: flex;
    align-items: center;
    padding: 12px 24px;
    font-size: 16px;
    border: 2px solid #6D64FF;
    background: none;
    color: #6d64ff;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.2s;
}

.upload-btn i,
.record-btn i {
    margin-left: 8px;
}

.upload-btn:hover,
.record-btn:hover {
    background: rgba(109, 100, 255, 0.1)
}

.stop-btn {
    background-color: #ff4d4f;
    border-color: #ff4d4f;
    color: #ffffff;
}

.stop-btn:hover {
    color: #ff4d4f;
    background-color: rgba(255, 77, 79, 0.1);
}

.recording-visuals {
    display: flex;
    flex-direction: column;
    width: 100%;
    align-items: center;
    gap: 12px;
    margin-top: 12px;
}

.record-timer {
    font-size: 14px;
    color: #6d64ff;
    font-weight: 500;
}

.audio-history {
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-top: 24px;
    border-top: 1px solid #eee;
    padding-top: 12px;
}

.audio-history-title {
    font-size: 18px;
    font-weight: 600;
    color: #6d64ff;
    margin-bottom: 8px;
}

.audio-history-row {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 0.6rem 0.6rem 0.6rem 1.2rem;
    padding-top: 2rem;
    padding-bottom: 2rem;
}

.audio-history-row.selected {
    border: 2px solid var(--purple);
    border-radius: 8px;
}

.audio-history-time {
    flex: 1 1 0;
    text-align: left;
    color: var(--text-primary);
}

.audio-history-duration {
    flex: 1 1 0;
    text-align: center;
    color: var(--text-primary);
}

.select-btn-area {
    flex: 1 1 0;
    display: flex;
    justify-content: flex-end;
}

.select-btn {
    color: var(--text-primary);
    cursor: pointer;
}

.select-btn.selected {
    background: var(--purple);
    color: #fff;
    padding: 4px 12px;
    border-radius: 4px;
    border-color: var(--purple);
}

.select-btn:hover:not(.selected) {
    color: var(--purple);
}

.audio-history-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin-top: 48px;
    margin-bottom: 48px;
    color: #888;
}

.empty-img {
    width: 80px;
    height: 80px;
    margin-bottom: 18px;
    opacity: 0.7;
}

.empty-text {
    font-size: 1.1rem;
    text-align: center;
    color: #888;
    max-width: 320px;
}
</style>
